"use client"

import * as React from "react"
import {
  DayPicker,
  dateMatchModifiers,
  getDefaultClassNames,
  useDayPicker,
  type DayButton,
  type Locale,
  type Matcher,
} from "react-day-picker"

import { cn } from "../lib/utils"
import { Button, buttonVariants } from "./button"
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from "lucide-react"

/**
 * Status tone for a day indicator. Maps onto the design system's status
 * tokens rather than raw colours, so a consumer app's theme drives the hue.
 */
export type CalendarIndicatorTone =
  | "destructive"
  | "warning"
  | "info"
  | "success"

export interface CalendarDayIndicator {
  /** Stable key. Also becomes the underlying day-picker modifier name. */
  id: string
  /**
   * Which dates carry the indicator. Any day-picker `Matcher`, so a
   * `Date[]`, a range, or a predicate all work. Prefer a predicate when the
   * host reasons in `YYYY-MM-DD` strings, since that sidesteps the
   * timezone traps of comparing `Date` timestamps.
   */
  match: Matcher
  tone: CalendarIndicatorTone
  /**
   * Legend copy. Omit to draw the dot with no legend row. The legend only
   * renders rows for indicators that actually match a date in the month on
   * screen, so it never explains a dot the guest cannot see.
   */
  label?: string
}

/**
 * Dot colours reference the status tokens through raw `var()` with a hex
 * fallback, matching how the day button handles `--primary`: consumer apps
 * wrap tokens in hsl()/oklch() at the config layer, which breaks the
 * `bg-destructive` utility but leaves `var(--destructive)` intact.
 */
const INDICATOR_DOT_CLASS: Record<CalendarIndicatorTone, string> = {
  destructive: "bg-[var(--destructive,#e13d3d)]",
  warning: "bg-[var(--warning,#ee973a)]",
  info: "bg-[var(--info,#71a5e5)]",
  success: "bg-[var(--success,#68b182)]",
}

const INDICATOR_MODIFIER_PREFIX = "indicator-"

/**
 * How far a finger has to travel, in CSS px, before a touch counts as a
 * month swipe rather than a tap on a day or a nudge while scrolling.
 */
const SWIPE_MIN_DISTANCE = 40

/**
 * The `Months` container with touch-swipe month paging (`swipeNavigation`).
 *
 * A finger swipe LEFT goes to the next month, RIGHT to the previous — the
 * same two moves the caption arrows make, read off day-picker's own context:
 * `nextMonth` / `previousMonth` are already clamped to `startMonth` /
 * `endMonth`, so at the first allowed month `previousMonth` is `undefined`
 * and a back-swipe does nothing, exactly as the disabled arrow does. No
 * bound is re-derived here.
 *
 * Only a PREDOMINANTLY horizontal move of at least `SWIPE_MIN_DISTANCE`
 * pages: a tap on a day travels nowhere, and a vertical scroll through a
 * sheet that holds the calendar must not flip the month on the way past.
 * Touch events only — a mouse drag over a calendar is a selection gesture
 * elsewhere in the system, not a page.
 *
 * Must render INSIDE `DayPicker` (it is passed as `components.Months`) for
 * `useDayPicker` to have a context.
 */
function SwipeableMonths({
  onTouchStart,
  onTouchEnd,
  ...props
}: React.ComponentProps<"div">) {
  const { nextMonth, previousMonth, goToMonth } = useDayPicker()
  const start = React.useRef<{ x: number; y: number } | null>(null)

  return (
    <div
      {...props}
      onTouchStart={(event) => {
        onTouchStart?.(event)
        const touch = event.touches[0]
        start.current = touch ? { x: touch.clientX, y: touch.clientY } : null
      }}
      onTouchEnd={(event) => {
        onTouchEnd?.(event)
        const from = start.current
        start.current = null
        const touch = event.changedTouches[0]
        if (!from || !touch) return
        const dx = touch.clientX - from.x
        const dy = touch.clientY - from.y
        if (
          Math.abs(dx) < SWIPE_MIN_DISTANCE ||
          Math.abs(dx) < Math.abs(dy) * 1.5
        ) {
          return
        }
        if (dx < 0 && nextMonth) goToMonth(nextMonth)
        else if (dx > 0 && previousMonth) goToMonth(previousMonth)
      }}
    />
  )
}

/** True when any day of `month` satisfies the matcher. */
function monthHasMatch(month: Date, match: Matcher): boolean {
  const year = month.getFullYear()
  const monthIndex = month.getMonth()
  const lastDay = new Date(year, monthIndex + 1, 0).getDate()
  for (let day = 1; day <= lastDay; day++) {
    if (dateMatchModifiers(new Date(year, monthIndex, day), match)) return true
  }
  return false
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  locale,
  formatters,
  components,
  dayIndicators,
  showIndicatorLegend = false,
  swipeNavigation = false,
  modifiers,
  month,
  defaultMonth,
  onMonthChange,
  numberOfMonths,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
  /** Small status dots drawn under the day number. */
  dayIndicators?: CalendarDayIndicator[]
  /**
   * Render a legend beneath the grid for the labelled indicators visible in
   * the month on screen. Off by default, so existing callers are unchanged.
   */
  showIndicatorLegend?: boolean
  /**
   * Page months with a horizontal touch swipe (left = next, right =
   * previous), for a calendar on a phone where the caption arrows are a
   * reach. Honours `startMonth` / `endMonth` exactly as the arrows do — at
   * the first allowed month a back-swipe is a no-op. Off by default.
   */
  swipeNavigation?: boolean
}) {
  const defaultClassNames = getDefaultClassNames()

  // Track the visible month so the legend can filter to it. `month` (a
  // controlled caller) always wins; otherwise we mirror day-picker's own
  // uncontrolled behaviour and seed from defaultMonth.
  const [internalMonth, setInternalMonth] = React.useState<Date>(
    () => month ?? defaultMonth ?? new Date()
  )
  const visibleMonth = month ?? internalMonth

  const handleMonthChange = React.useCallback(
    (next: Date) => {
      setInternalMonth(next)
      onMonthChange?.(next)
    },
    [onMonthChange]
  )

  // Each indicator becomes a day-picker modifier, which is how the day
  // button learns whether a given date carries it. The prefix keeps these
  // from colliding with a caller's own modifier names.
  const indicatorModifiers = React.useMemo(() => {
    if (!dayIndicators?.length) return undefined
    return Object.fromEntries(
      dayIndicators.map((indicator) => [
        `${INDICATOR_MODIFIER_PREFIX}${indicator.id}`,
        indicator.match,
      ])
    )
  }, [dayIndicators])

  const mergedModifiers =
    indicatorModifiers || modifiers
      ? { ...indicatorModifiers, ...modifiers }
      : undefined

  const legendItems = React.useMemo(() => {
    if (!showIndicatorLegend || !dayIndicators?.length) return []
    const months: Date[] = []
    for (let offset = 0; offset < (numberOfMonths ?? 1); offset++) {
      months.push(
        new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1)
      )
    }
    return dayIndicators.filter(
      (indicator) =>
        indicator.label &&
        months.some((m) => monthHasMatch(m, indicator.match))
    )
  }, [showIndicatorLegend, dayIndicators, visibleMonth, numberOfMonths])

  const picker = (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        // Cell + container scale up from the react-day-picker default
        // (28px / p-2) so the day grid breathes — 28px buttons sit
        // cramped next to the popover's other text, especially with
        // the today-fill primary chip. 36px is the smallest comfortable
        // step; p-3 + week mt-3 give the rows + edges matching air.
        "group/calendar bg-background p-3 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(9)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code, { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative rounded-(--cell-radius)",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "absolute inset-0 bg-popover opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "font-medium select-none",
          captionLayout === "label"
            ? "text-sm"
            : "flex items-center gap-1 rounded-(--cell-radius) text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          // `text-foreground/70` adapts to mode: dark on light bg,
          // ~white-with-30%-transparency on dark bg. The previous
          // `text-muted-foreground` token was too dim to read on
          // dark surfaces inside the booking calendar popovers.
          "flex-1 select-none rounded-(--cell-radius) text-[0.8rem] font-normal text-foreground/70",
          defaultClassNames.weekday
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-(--cell-size) select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-[0.8rem] text-muted-foreground select-none",
          defaultClassNames.week_number
        ),
        day: cn(
          // `flex items-center justify-center` so the explicit-size
          // day button (see CalendarDayButton below) sits dead-centre in
          // the cell instead of hugging the left edge when the cell is
          // wider than --cell-size. Drop `h-full w-full` — having both
          // axes explicit kills `aspect-square` per CSS spec; `w-(--cell-size)`
          // alone lets aspect-square derive the height, same square shape.
          //
          // The explicit width matters for a `hidden` cell (an outside day
          // with `showOutsideDays={false}`): react-day-picker renders those
          // as an empty `<td>` with no button inside, so without a width of
          // its own the cell had nothing to size itself against and
          // collapsed to zero — which pulled every real date in that week
          // left, out from under its correct weekday column. A month whose
          // 1st isn't a Sunday showed its whole first row under the wrong
          // headers.
          "group/day relative flex aspect-square w-(--cell-size) items-center justify-center rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)",
          props.showWeekNumber
            ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)"
            : "[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)",
          defaultClassNames.day
        ),
        range_start: cn(
          "relative isolate z-0 rounded-l-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-muted",
          defaultClassNames.range_start
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn(
          "relative isolate z-0 rounded-r-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-muted",
          defaultClassNames.range_end
        ),
        today: cn(
          // Cell-level today bg removed — with the day cell now flex-
          // centring its (smaller) day button, a cell-wide bg looks like
          // a square wrapping nothing. Today styling lives on the button
          // itself via `data-[today=true]` further down.
          "text-foreground",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            )
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon className={cn("size-4", className)} {...props} />
            )
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          )
        },
        DayButton: ({ ...props }) => (
          <CalendarDayButton
            locale={locale}
            dayIndicators={dayIndicators}
            {...props}
          />
        ),
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...(swipeNavigation ? { Months: SwipeableMonths } : null),
        ...components,
      }}
      modifiers={mergedModifiers}
      month={month}
      defaultMonth={defaultMonth}
      onMonthChange={handleMonthChange}
      numberOfMonths={numberOfMonths}
      {...props}
    />
  )

  // Callers without a legend keep the exact DOM they always had. The wrapper
  // decision is made from PROPS, never from what the month on screen happens
  // to contain: toggling the root between a bare DayPicker and a wrapping
  // div as the user pages through months remounts the DayPicker, which
  // resets its internal month to the start. On the booking page that read
  // as "the calendar only goes three months ahead" (the first month with a
  // marker was December; stepping into it snapped back to September).
  const legendEnabled = showIndicatorLegend && Boolean(dayIndicators?.length)
  if (!legendEnabled) return picker

  return (
    <div className="flex flex-col">
      {picker}
      {legendItems.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-[var(--border,rgba(0,0,0,0.06))] p-3">
          {legendItems.map((indicator) => (
            <span
              key={indicator.id}
              className="flex items-center gap-1.5 text-xs text-[var(--muted-foreground,#6b6b6b)]"
            >
              <span
                aria-hidden
                className={cn(
                  "size-1.5 shrink-0 rounded-full",
                  INDICATOR_DOT_CLASS[indicator.tone]
                )}
              />
              {indicator.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  dayIndicators,
  children,
  ...props
}: React.ComponentProps<typeof DayButton> & {
  locale?: Partial<Locale>
  dayIndicators?: CalendarDayIndicator[]
}) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  // Every indicator whose modifier is set for this date. A date can carry
  // more than one (a modified-hours date that also hosts an event), so the
  // dots sit in a row rather than one winning by priority.
  const activeIndicators = (dayIndicators ?? []).filter(
    (indicator) => modifiers[`${INDICATOR_MODIFIER_PREFIX}${indicator.id}`]
  )

  return (
    <Button
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-today={modifiers.today}
      className={cn(
        // Explicit square size locked to --cell-size so `aspect-ratio`
        // doesn't have to fight with parent dimensions. Centred content
        // via items-center/justify-center; flex-col preserved for the
        // optional secondary span (see [&>span]:text-xs further down).
        // Visual states stacked so the last-matching rule wins via
        // Tailwind source order:
        //   1. selected (single)  → subtle foreground/10 fill + bold
        //      (matches the ghost-button language used elsewhere)
        //   2. today              → primary-coloured filled circle.
        //      Uses `--primary` so consumer apps (booking with venue
        //      brand kits, Connect with new-ui-theme) get the right
        //      tone automatically without overrides.
        //   3. today + selected   → primary fill + foreground ring so
        //      the "selected" semantic still reads on top
        //
        // Range states (start/middle/end) keep the primary fill since
        // they read as a contiguous picked region.
        //
        // Colour utilities use raw CSS vars (`bg-[var(--primary)]`)
        // rather than `bg-primary` so they survive consumer apps that
        // wrap tokens in hsl()/oklch() at the tailwind config layer
        // (Connect does this; the legacy hsl(var(--primary)) breaks
        // when the new-ui-theme overrides `--primary` to a hex value).
        "relative isolate z-10 flex size-(--cell-size) flex-col items-center justify-center gap-1 border-0 text-[1rem]! leading-none font-medium text-[var(--foreground,#1f1f1f)] group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50 data-[selected-single=true]:bg-[var(--primary,#1f1f1f)] data-[selected-single=true]:text-[var(--primary-foreground,#fff)] data-[selected-single=true]:font-semibold data-[today=true]:bg-[color-mix(in_oklab,var(--primary,#1f1f1f)_18%,transparent)] data-[today=true]:text-[var(--foreground,#1f1f1f)] data-[today=true]:font-semibold data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-[var(--primary,#1f1f1f)] data-[range-end=true]:text-[var(--primary-foreground,#fff)] data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-[var(--muted,#e6ded1)] data-[range-middle=true]:text-[var(--foreground,#1f1f1f)] data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-[var(--primary,#1f1f1f)] data-[range-start=true]:text-[var(--primary-foreground,#fff)] data-[today=true]:data-[selected-single=true]:bg-[var(--primary,#1f1f1f)] data-[today=true]:data-[selected-single=true]:text-[var(--primary-foreground,#fff)] data-[today=true]:data-[selected-single=true]:ring-2 data-[today=true]:data-[selected-single=true]:ring-[var(--foreground,#1f1f1f)]/30 data-[today=true]:data-[selected-single=true]:ring-inset dark:hover:text-[var(--foreground)] [&>span]:text-[1rem]",
        defaultClassNames.day,
        className
      )}
      {...props}
    >
      {children}
      {activeIndicators.length > 0 ? (
        <span
          aria-hidden
          // Absolutely positioned so the dots never shift the day number,
          // and pointer-events-none so they can't swallow the day's click.
          className="pointer-events-none absolute bottom-1 left-1/2 flex -translate-x-1/2 items-center gap-0.5 leading-none"
        >
          {activeIndicators.map((indicator) => (
            <span
              key={indicator.id}
              className={cn(
                "size-1.5 shrink-0 rounded-full",
                INDICATOR_DOT_CLASS[indicator.tone]
              )}
            />
          ))}
        </span>
      ) : null}
    </Button>
  )
}

export { Calendar, CalendarDayButton }
